import {
  FinnContact,
  FinnIndustry,
  FinnJobTypes, FinnManagerTypes,
  FinnOccupation,
  FinnPartnerId
} from '../../finn/models/finn.model';
import { GooglePlaceDetail, GooglePrediction } from '../../shared/services/google/places.service';
import { Company, CompanyLanguage, ImageSize, ShortCompanyInJob } from '../company/company.model';
import { Image } from '../../shared/models/cfile/cfile.model';
import { User } from '../../shared/models/user/user.model';
import { PlaceID } from './job-templates.model';
import { JobtipAd } from '../../jobtip/models/jobtip.model';
import { Qualification } from '../../shared/models/qualification/qualification.model';
import { TLangCode } from '../../ngrx/locale/states/locale.state';
import { PromotionName, PromotionStatus } from '../../promotion/interfaces/promotion-provider.interface';
import { AiScoringTriggerType } from '../ai/ai.model';



export interface Benefit {
  id?: number;
  icon: string;
  heading: string;
  description?: string;
}

export interface JobRequirement {
    id: string | number;
    name: string;
    option: 'optional' | 'required' | 'excluded';
    translations: Partial<Record<TLangCode, { name: string }>>;
}

export interface JobTranslations {
  title: string;
  header: string;
  description: string;
  apply_button_text: string;
}

export class Finn {
    finn_url: string;
    expired_at: string;
    id: number;
    published: boolean;
    stopped: boolean;
}

export interface DeletedApplication {
    applicant: User;
    assessment_folder: {
        id: number;
        name: string;
    };
}

export interface JobAdvancedFields {
  theme: 'light' | 'dark';
  translations: Partial<Record<TLangCode, { apply_button_text: string }>>;
  apply_button_place: string;
  branding_color: string;
  button_roundings: number;
  is_add_gradient: boolean;
  benefits_header: string;
  benefits: Benefit[];
  gallery_images: Image[];
  is_add_gallery_bg: boolean;
  schedule: boolean;
  logo_shadow: string;
  logo_size: ImageSize;
  logo_position: 0 | 1 | 2;
}

export interface JobAiScoringSettings {
  enabled: boolean;
  settings_id: string;
  trigger_type: AiScoringTriggerType;
}

export class Job {
    id: number;
    title?: string;
    header?: string;
    description?: string;
    ai_scoring: JobAiScoringSettings;
    button_roundings?: number;
    languages?: CompanyLanguage[];
    external: boolean;
    internal: boolean;
    company: ShortCompanyInJob;
    ad_image: any;
    ad_image_mobile?: any;
    expiration_date: string;
    apply_asap: boolean;
    requirements: string;
    is_published: boolean;
    requires_video: boolean;
    num_of_applicants: number;
    employees: User[];
    job_requirements: JobRequirement[];
    questions: Qualification[];
    was_ever_published: boolean;
    place_id?: string | GooglePrediction | GooglePlaceDetail;
    finn: Finn;
    youtube_video_link: string;
    ongoing: boolean;
    can_edit: boolean;
    gobi_videos?: any[];
    deleted_applications: DeletedApplication[];
    use_specific_mail_template: boolean;
    job_tab?: number;
    theme: 'light' | 'dark';
    apply_button_text: string;
    apply_button_place: string;
    branding_color: string;
    is_add_gradient: boolean;
    benefits_header: string;
    benefits: Benefit[];
    gallery_images: Image[];
    is_add_gallery_bg: boolean;
    schedule: boolean;
    preview_img_url: string;
    job_folder: number;
    logo_shadow?: string;
    font?: string;
    logo_size: ImageSize;
    logo_position: 0 | 1 | 2;
    finn_partner_id?: FinnPartnerId;
    places_id: PlaceID[];
    jobtip_ad?: JobtipAd;
    use_external_apply_link?: boolean;
    external_apply_link?: string;
    translations: Record<TLangCode, JobTranslations>;
    is_hidden_from_mother_cp: boolean;

  constructor(
    ) {

    }
}


export interface FinnJob {
    id: number;
    finn_partner_id?: string;
    partner_id_name?: string;
    partner_id?: string;
    company?: Company;
    industries: FinnIndustry[];
    tags: string[];
    occupations: FinnOccupation[];
    job_contacts: FinnContact[];
    job_type: FinnJobTypes;
    application_email: string;
    company_url: string;
    facebook_url: string;
    linkedin_url: string;
    twitter_handle?: string;
    video_url: string;
    ingress: string;
    images: Image[];
    order_no: string;
    title: string;
    header: string;
    description: string;
    street_address: string;
    zip_code: string;
    country: string;
    country_code: string;
    application_url?: string;
    number_of_positions: number;
    sector: string;
    duration: string;
    stopped: boolean;
    published: boolean;
    finn_url: string;
    invoice_reference: string;
    advert_start_date: string;
    advert_stop_date: string;
    expired_at: string;
    expiration_date: string;
    first_publish_by: {
      first_name: string;
      last_name: string;
    };
    statistics_link: string;
    is_expired: boolean;
    status: FinnStatus;
    tab_title?: {
      active_for: number,
      expires_in: number,
    },
    manager_role: FinnManagerTypes;
}

export enum FinnStatus {
  ACTIVE = 'active',
  NOT_ACTIVE = 'not_active',
  DRAFT = 'draft',
  EXPIRED = 'expired',
  PENDING = 'pending'
}

export interface DashboardJob {
  id: number;
  can_edit: boolean;
  deadline: string;
  header: string;
  is_published: boolean;
  job_tab: number;
  num_of_applicants: number;
  title: string;
}

export class JobPaginationData {
  count: number;
  next: string;
  previous: string;
  results: Job[];
}

export class DashboardJobPaginationData {
  count: number;
  next: string;
  previous: string;
  results: DashboardJob[];
}

export type AdministrateJob = {
  id: number;
  header: string;
  title: string;
};

export interface JobLanguage {
  code: TLangCode,
  name: string;
  is_default?: boolean;
}

export interface JobInternal {
  id: number;
  title: string;
  ad_image: Image;
  company: JobInternalCompany;
}

interface JobInternalCompany {
  name: string;
  search_name: string;
}

export interface JobInternalPagination {
  count: number;
  next: string;
  previous: string;
  results: JobInternal[];
}

export interface JobPromotion {
  code: PromotionName;
  entity_id: number;
  name: string;
  status: PromotionStatus;
  expires_in?: number;
  active_for?: number;
}
